import { DateRange, DynamicFeed } from '@mui/icons-material';
import { Button, List, ListItem, Popover, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DateTime } from 'luxon';
import { type FC, useEffect, useState } from 'react';
import { readableDate } from 'utils';
import type { FaultInteractionData } from '../../models';

type AvaniDropdownProps = {
  faultInteractions: FaultInteractionData[];
  activeInteraction: FaultInteractionData;
  setActiveInteraction: (v: FaultInteractionData) => void;
  onSelectDate?: (date: string | null) => void;
  onSelectOption?: (value: string | number | null) => void;
  buttonSx?: any;
  dates?: boolean;
  options?: { name: string; value: string | number }[];
  togglerLabel?: string;
};

const StyledButton = styled(Button)(
  ({ theme }) => `
    background-color: ${theme.colors.avani.turc};
    border-radius: 8px;
    border: none;
    color: white;
    padding: 8px 12px;

    &:hover {
      border: none;
      color: ${theme.colors.avani.darkTitleText};

      .MuiSvgIcon-root {
        color: ${theme.colors.avani.darkTitleText};
      }
    }
`
);

const StyledListItem = styled(ListItem)(
  ({ theme }) => `
  cursor: pointer;
  display: flex;

  svg {
    margin-right: 12px;
  }
`
);

export const FaultInteractionsDropdown: FC<AvaniDropdownProps> = ({
  faultInteractions,
  activeInteraction,
  setActiveInteraction,
  buttonSx,
  dates,
  togglerLabel
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const [buttonLabel, setButtonLabel] = useState(dates ? 'Select Date' : togglerLabel);

  useEffect(() => {
    if (!activeInteraction) return;
    const isISO = activeInteraction.startedAt.includes('-');
    const dateDisplay = isISO
      ? DateTime.fromISO(activeInteraction.startedAt).toFormat('dd/MM/yyyy')
      : activeInteraction.startedAt;
    setButtonLabel(dateDisplay);
  }, [activeInteraction]);

  const selectInteraction = (interactionId: string) => {
    const foundActive = faultInteractions.find(({ id }) => id === interactionId);
    if (!foundActive) return;

    const isISO = foundActive.startedAt.includes('-');
    const dateDisplay = isISO ? DateTime.fromISO(foundActive.startedAt).toFormat('dd/MM/yyyy') : foundActive.startedAt;

    setButtonLabel(dateDisplay);
    setAnchorEl(null);
    if (foundActive) setActiveInteraction(foundActive);
  };

  return (
    <>
      <StyledButton variant="outlined" onClick={(e) => setAnchorEl(e.currentTarget)} sx={buttonSx}>
        {dates && faultInteractions.length > 1 ? <DynamicFeed sx={{ mr: 1 }} /> : <DateRange sx={{ mr: 1 }} />}
        {buttonLabel}
      </StyledButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
      >
        <List>
          {dates &&
            faultInteractions.map((interaction) => {
              return (
                <StyledListItem
                  key={interaction.id}
                  value={interaction.id}
                  onClick={() => selectInteraction(interaction.id)}
                >
                  <DateRange sx={{ mr: 1 }} />
                  <Typography sx={{ flexGrow: 1, pt: '3px' }}>{readableDate(interaction.startedAt)}</Typography>
                </StyledListItem>
              );
            })}
        </List>
      </Popover>
    </>
  );
};
